import { Fragment, type ReactNode } from 'react';
import { DateTime, Num, T, Var, useGT } from 'gt-react';
import type { SuggestionCostAgreement } from '@erp/rpc';
import type { LegPriceBreak } from '@erp/shared-types';
import { formatCurrency, parseDateOnlyLocal } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import {
  agreementSavingOf,
  type AgreementSaving,
} from '../components/agreementSaving';
import {
  formatUnitCost,
  formatWholePercent,
} from '../components/unitCostFormat';

/**
 * What the supplier cost agreement behind a buy line offers, in the
 * suggestion detail modal (docs/procurement/forward-buying.md, Part 2,
 * decision 17): which contract line it is, what it saves against the supplier
 * product row's own price for the same quantity, where and until when it
 * applies, the MOQ and multiple it imposes, and its tiers.
 *
 * The prices are the ones the engine froze on the leg; the contract's line,
 * scope, window, terms and tiers are read live from the agreement.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const WINDOW_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/**
 * Whole days from `today` to the agreement's last valid day, both read as
 * local calendar dates: 0 on the last day, negative once it has ended.
 */
export function daysUntilDateOnly(
  dateOnly: string,
  today: Date,
): number | null {
  const end = parseDateOnlyLocal(dateOnly);
  return end === null ? null : daysUntil(end, today);
}

/** {@link daysUntilDateOnly} on an already parsed local-midnight date. */
function daysUntil(end: Date, today: Date): number {
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  // Rounded: a daylight-saving change makes one local day 23 or 25 hours.
  return Math.round((end.getTime() - start.getTime()) / DAY_MS);
}

function positiveOrNull(value: number | null): number | null {
  return value !== null && value > 0 ? value : null;
}

function ContractHeading({
  contractNumber,
  agreement,
}: {
  contractNumber: number | null;
  agreement: SuggestionCostAgreement | null;
}) {
  return (
    <p className="font-medium text-gray-900">
      {contractNumber === null ? (
        <T context="suggestion detail contract block heading: a supplier cost agreement priced this buy; its contract number is unknown">
          Contract price
        </T>
      ) : agreement === null ? (
        <T context="suggestion detail contract block heading: the supplier contract number that priced this buy">
          Contract <Var>{String(contractNumber)}</Var>
        </T>
      ) : (
        <T context="suggestion detail contract block heading: the supplier contract number and its line number that priced this buy">
          Contract <Var>{String(contractNumber)}</Var> · line{' '}
          <Var>{String(agreement.contractLine)}</Var>
        </T>
      )}
      {agreement?.description != null && (
        <span className="ml-2 font-mono font-normal text-gray-500">
          {agreement.description}
        </span>
      )}
    </p>
  );
}

function SavingLine({ saving }: { saving: AgreementSaving | null }) {
  if (saving === null) {
    return (
      <p className="text-gray-500">
        <T context="suggestion detail contract block: the engine run that priced this buy did not record the price without the contract, so no saving can be shown">
          The price without this contract was not recorded for this run.
        </T>
      </p>
    );
  }
  const baseline = formatUnitCost(saving.withoutAgreementUnitCost);
  const contract = formatUnitCost(saving.agreementUnitCost);
  const onList = saving.withoutAgreementCostSource === 'list';

  if (saving.totalSaving > 0) {
    const total = formatCurrency(saving.totalSaving);
    // A positive saving means the row costs more than the contract, so more
    // than zero: the share saved always exists here.
    const percent = formatWholePercent(saving.fractionSaved ?? 0);
    return (
      <p className="font-medium text-green-700">
        {onList ? (
          <T context="suggestion detail contract block: the supplier's list unit cost, the contract unit cost, what the contract saves on the whole buy, the buy quantity, and the share of the list cost saved">
            List <Var>{baseline}</Var> → contract <Var>{contract}</Var> · saves{' '}
            <Var>{total}</Var> on <Num>{saving.quantity}</Num> (
            <Var>{percent}</Var> off)
          </T>
        ) : (
          <T context="suggestion detail contract block: the supplier's price-break unit cost for this quantity, the contract unit cost, what the contract saves on the whole buy, the buy quantity, and the share saved">
            Price break <Var>{baseline}</Var> → contract <Var>{contract}</Var> ·
            saves <Var>{total}</Var> on <Num>{saving.quantity}</Num> (
            <Var>{percent}</Var> off)
          </T>
        )}
      </p>
    );
  }

  if (saving.totalSaving < 0) {
    const extra = formatCurrency(-saving.totalSaving);
    return (
      <p className="font-medium text-amber-700">
        {onList ? (
          <T context="suggestion detail contract block warning: the contract unit cost is higher than the supplier's list unit cost, and how much more the whole buy costs because of it; the contract still applies">
            Contract <Var>{contract}</Var> is above list <Var>{baseline}</Var> ·
            costs <Var>{extra}</Var> more on <Num>{saving.quantity}</Num>
          </T>
        ) : (
          <T context="suggestion detail contract block warning: the contract unit cost is higher than the supplier's price-break unit cost for this quantity, and how much more the whole buy costs because of it; the contract still applies">
            Contract <Var>{contract}</Var> is above the price break{' '}
            <Var>{baseline}</Var> · costs <Var>{extra}</Var> more on{' '}
            <Num>{saving.quantity}</Num>
          </T>
        )}
      </p>
    );
  }

  return (
    <p className="text-gray-700">
      <T context="suggestion detail contract block: the contract unit cost equals the supplier's own price for this quantity, so it saves nothing">
        Contract <Var>{contract}</Var> matches the supplier's own price · no
        saving
      </T>
    </p>
  );
}

function ScopeLabel({ agreement }: { agreement: SuggestionCostAgreement }) {
  if (agreement.scopeKind === 'warehouse') {
    return agreement.warehouseName === null ? (
      <T context="suggestion detail contract block: the contract applies to a single branch whose name is unknown">
        Applies to one branch only
      </T>
    ) : (
      <T context="suggestion detail contract block: the contract applies to this one branch only">
        Applies to <Var>{agreement.warehouseName}</Var> only
      </T>
    );
  }
  if (agreement.scopeKind === 'division') {
    return (
      <T context="suggestion detail contract block: the contract applies to every branch of this division">
        Applies across division <Var>{agreement.divisionCode}</Var>
      </T>
    );
  }
  return (
    <T context="suggestion detail contract block: the contract applies to a district of branches">
      Applies to a district
    </T>
  );
}

function EndsIn({ days }: { days: number }) {
  if (days > 0) {
    return (
      <T context="suggestion detail contract block: how many days remain until the contract's last valid day">
        ends in <Num>{days}</Num> days
      </T>
    );
  }
  if (days === 0) {
    return (
      <T context="suggestion detail contract block: today is the contract's last valid day">
        ends today
      </T>
    );
  }
  return (
    <T context="suggestion detail contract block: the contract's last valid day has passed">
      ended
    </T>
  );
}

function WindowDates({ start, end }: { start: Date | null; end: Date }) {
  if (start === null) {
    return (
      <T context="suggestion detail contract block: the last day the contract is valid">
        valid through <DateTime options={WINDOW_DATE_FORMAT}>{end}</DateTime>
      </T>
    );
  }
  return (
    <T context="suggestion detail contract block: the first and last day the contract is valid">
      valid <DateTime options={WINDOW_DATE_FORMAT}>{start}</DateTime> –{' '}
      <DateTime options={WINDOW_DATE_FORMAT}>{end}</DateTime>
    </T>
  );
}

/** Where the contract applies, then its window and how long is left of it. */
function WindowLine({
  agreement,
  endsOn,
  today,
}: {
  agreement: SuggestionCostAgreement | null;
  endsOn: string | null;
  today: Date;
}) {
  const parts: ReactNode[] = [];
  if (agreement !== null) {
    parts.push(<ScopeLabel key="scope" agreement={agreement} />);
  }
  const end = endsOn === null ? null : parseDateOnlyLocal(endsOn);
  if (end !== null) {
    const start =
      agreement === null ? null : parseDateOnlyLocal(agreement.effectiveStart);
    parts.push(<WindowDates key="dates" start={start} end={end} />);
    parts.push(<EndsIn key="ends" days={daysUntil(end, today)} />);
  }
  if (parts.length === 0) return null;
  return (
    <p>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && ' · '}
          {part}
        </Fragment>
      ))}
    </p>
  );
}

function TermsLine({ agreement }: { agreement: SuggestionCostAgreement }) {
  const moq = positiveOrNull(agreement.minQuantity);
  const multiple = positiveOrNull(agreement.incrementalQuantity);
  if (moq === null && multiple === null) {
    return (
      <p className="text-gray-500">
        <T context="suggestion detail contract block: the contract sets no minimum order quantity and no order multiple">
          No contract MOQ or order multiple
        </T>
      </p>
    );
  }
  return (
    <p>
      {moq !== null && multiple !== null ? (
        <T context="suggestion detail contract block: the minimum order quantity and the order multiple the contract requires">
          Contract MOQ <Num>{moq}</Num> · multiple of <Num>{multiple}</Num>
        </T>
      ) : moq !== null ? (
        <T context="suggestion detail contract block: the minimum order quantity the contract requires">
          Contract MOQ <Num>{moq}</Num>
        </T>
      ) : (
        <T context="suggestion detail contract block: the order multiple the contract requires">
          Contract multiple of <Num>{multiple}</Num>
        </T>
      )}
    </p>
  );
}

function TiersLine({
  agreement,
  currentTier,
}: {
  agreement: SuggestionCostAgreement;
  currentTier: number | null;
}) {
  const gt = useGT();
  if (agreement.tiers.length < 2) return null;
  return (
    <p className="flex flex-wrap gap-x-2">
      <span className="text-gray-500">
        {gt('Tiers', {
          context:
            'suggestion detail contract block: label before the list of contract price tiers',
        })}
      </span>
      {agreement.tiers.map((tier) => (
        <span
          key={tier.tier}
          className={cn(
            'tabular-nums',
            tier.tier === currentTier && 'font-semibold text-gray-900',
          )}
        >
          <T context="suggestion detail contract block: one contract price tier, the quantity it starts at and its unit cost">
            <Num>{tier.minQuantity}</Num>+{' '}
            <Var>{formatUnitCost(tier.unitCost)}</Var>
          </T>
        </span>
      ))}
    </p>
  );
}

interface ContractPricingDetailProps {
  priceBreak: LegPriceBreak | null | undefined;
  costAgreement: SuggestionCostAgreement | null | undefined;
  quantity: number | null | undefined;
  /** The day the window is counted from; today unless a test pins it. */
  today?: Date;
}

export function ContractPricingDetail({
  priceBreak,
  costAgreement,
  quantity,
  today = new Date(),
}: ContractPricingDetailProps) {
  if (priceBreak == null || priceBreak.costSource !== 'agreement') return null;
  const agreement = costAgreement ?? null;
  return (
    <div className="col-span-2 space-y-1 rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-700">
      <ContractHeading
        contractNumber={agreement?.contractNumber ?? priceBreak.contractNumber}
        agreement={agreement}
      />
      <SavingLine saving={agreementSavingOf({ priceBreak, quantity })} />
      <WindowLine
        agreement={agreement}
        endsOn={agreement?.effectiveEnd ?? priceBreak.constraintEndsOn}
        today={today}
      />
      {agreement !== null && <TermsLine agreement={agreement} />}
      {agreement !== null && (
        <TiersLine agreement={agreement} currentTier={priceBreak.tier} />
      )}
    </div>
  );
}
